import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { List, useTable } from '@refinedev/antd';
import {
  Table, Space, Tag, Input, Drawer, Form, Switch, Button, Select, Tabs,
  Collapse, Typography, message, theme, Radio, Upload, Checkbox, Spin, Popconfirm,
} from 'antd';
import {
  SearchOutlined,
  ThunderboltOutlined, UploadOutlined, CheckCircleOutlined,
  DeleteOutlined, EditOutlined,
} from '@ant-design/icons';
import { useInvalidate } from '@refinedev/core';
import { axiosInstance, API_BASE_URL } from '../../../providers/dataProvider';
import { parseSections, joinSections, extractVariables, PromptSections } from './prompt-parser';
import { resizeImageForLlm } from '../utils/image-resize';


const tagStyle = (color: string) => ({
  borderColor: color, color, background: `${color}15`,
  borderRadius: 9999, padding: '2px 12px', fontWeight: 500,
});

export const LlmPromptList: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<any>(null);
  const [versions, setVersions] = useState<any[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [sections, setSections] = useState<PromptSections>({ system: '', user: '', output: '' });
  const [originalContent, setOriginalContent] = useState('');
  const [originalConfig, setOriginalConfig] = useState<any>({});
  const [versionConfig, setVersionConfig] = useState<any>({});
  const [activeTab, setActiveTab] = useState('edit');
  const invalidate = useInvalidate();
  const { token } = theme.useToken();
  const [form] = Form.useForm();

  // Providers (fetched directly from API, avoiding Refine data provider shape issues)
  const [providers, setProviders] = useState<any[]>([]);
  const [providersLoaded, setProvidersLoaded] = useState(false);
  // Buffer version content waiting for providers to load before populating config
  const [pendingVersion, setPendingVersion] = useState<{ v: any; vers?: any[] } | null>(null);

  // Test tab state
  const [testVars, setTestVars] = useState<Record<string, string>>({});
  const [testInputType, setTestInputType] = useState<'text' | 'image'>('text');
  const [testText, setTestText] = useState('');
  const [testImageBase64, setTestImageBase64] = useState<string | undefined>();
  const [, setTestImagePreview] = useState<string | undefined>();
  const [testProviders, setTestProviders] = useState<string[]>([]);
  const [testRunning, setTestRunning] = useState(false);
  const [testResults, setTestResults] = useState<any[]>([]);

  // Expanded versions panel
  const [selectedByPrompt, setSelectedByPrompt] = useState<Record<string, string[]>>({});
  const [deletingVersions, setDeletingVersions] = useState(false);

  // Fetch providers on mount
  useEffect(() => {
    axiosInstance.get(`${API_BASE_URL}/admin/llm/providers`)
      .then((resp) => {
        console.log('[LLM] providers raw response:', resp.data);
        const data = Array.isArray(resp.data) ? resp.data : (resp.data?.data ?? resp.data?.items ?? []);
        console.log('[LLM] providers parsed:', data.length, data);
        setProviders(data.filter((p: any) => p.isActive));
      })
      .catch((err) => {
        console.error('[LLM] providers fetch failed:', err?.response?.status, err?.message);
      })
      .finally(() => {
        setProvidersLoaded(true);
      });
  }, []);

  // Model list for selected provider
  const selectedProvider = providers.find((p: any) => p.id === versionConfig?.providerId);
  const modelOptions: string[] = useMemo(() => {
    const base: string[] = [];
    if (selectedProvider) {
      const list = selectedProvider.modelList;
      if (Array.isArray(list) && list.length > 0) base.push(...list);
      else if (selectedProvider.defaultModel) base.push(selectedProvider.defaultModel);
    }
    const savedModel = versionConfig?.model;
    if (savedModel && !base.includes(savedModel)) base.push(savedModel);
    return base;
  }, [selectedProvider, versionConfig?.model]);

  // Provider options — defensively include saved providerId even if it's been deactivated
  const providerOptions = useMemo(() => {
    const opts = providers.map((p: any) => ({ value: p.id, label: p.name }));
    const savedId = versionConfig?.providerId;
    if (savedId && !opts.find((o) => o.value === savedId)) {
      opts.push({ value: savedId, label: `${savedId} (inactive)` });
    }
    return opts;
  }, [providers, versionConfig?.providerId]);

  const { tableProps } = useTable({
    resource: 'llm-prompts',
    filters: { permanent: [...(searchText ? [{ field: 'search', operator: 'contains' as const, value: searchText }] : [])] },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  const detectedVars = useMemo(() => {
    const fullContent = joinSections(sections);
    return extractVariables(fullContent);
  }, [sections]);

  const openDrawer = async (record: any, targetVersionId?: string) => {
    setLoading(true);
    setDrawerOpen(true);
    setActiveTab('edit');
    setTestResults([]);
    setTestText('');
    setTestImageBase64(undefined);
    setTestImagePreview(undefined);
    form.setFieldsValue({ name: record.name, slug: record.slug, description: record.description, isActive: record.isActive });
    try {
      const resp = await axiosInstance.get(`${API_BASE_URL}/admin/llm/prompts/${record.id}`);
      const fullPrompt = resp.data;
      setSelectedPrompt(fullPrompt);
      const vers = fullPrompt.versions || [];
      setVersions(vers);
      const target = (targetVersionId && vers.find((v: any) => v.id === targetVersionId))
        || vers.find((v: any) => v.isActive)
        || vers[0];
      if (target) loadVersionContent(target, vers);
    } catch { message.error('Failed to load prompt'); }
    finally { setLoading(false); }
  };

  const loadVersionContent = (v: any, vers?: any[]) => {
    setSelectedVersionId(v.id);
    const parsed = parseSections(v.content);
    setSections(parsed);
    setOriginalContent(v.content);
    if (vers) setVersions(vers);
    // Wait for providers to load before populating config so dropdowns can resolve labels
    if (!providersLoaded) {
      setPendingVersion({ v, vers });
      setVersionConfig({});
      setOriginalConfig({});
      return;
    }
    const cfg = v.config || {};
    setVersionConfig(cfg);
    setOriginalConfig(cfg);
  };

  // Apply pending version config once providers finish loading
  useEffect(() => {
    if (!providersLoaded || !pendingVersion) return;
    const cfg = pendingVersion.v.config || {};
    console.log('[LLM] applying pending version config after providers loaded:', cfg);
    setVersionConfig(cfg);
    setOriginalConfig(cfg);
    setPendingVersion(null);
  }, [providersLoaded, pendingVersion]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedPrompt(null);
    setVersions([]);
    setSelectedVersionId(null);
    setSections({ system: '', user: '', output: '' });
    setOriginalContent('');
    setOriginalConfig({});
    setVersionConfig({});
    setPendingVersion(null);
    setTestResults([]);
    form.resetFields();
  };

  const handleVersionChange = (versionId: string) => {
    const v = versions.find((ver: any) => ver.id === versionId);
    if (v) loadVersionContent(v);
  };

  const handleSaveCurrent = async () => {
    if (!selectedPrompt || !selectedVersionId) return;
    const values = await form.validateFields();
    setSaving(true);
    try {
      await axiosInstance.patch(`${API_BASE_URL}/admin/llm/prompts/${selectedPrompt.id}`, values);
      const newContent = joinSections(sections);
      const configChanged = JSON.stringify(versionConfig) !== JSON.stringify(originalConfig);
      const contentChanged = newContent !== originalContent;
      if (contentChanged || configChanged) {
        await axiosInstance.patch(
          `${API_BASE_URL}/admin/llm/versions/${selectedVersionId}`,
          { content: newContent, config: versionConfig },
        );
      }
      message.success('Saved');
      invalidate({ resource: 'llm-prompts', invalidates: ['list'] });
      const full = await axiosInstance.get(`${API_BASE_URL}/admin/llm/prompts/${selectedPrompt.id}`);
      setSelectedPrompt(full.data);
      const reloadedVersions = full.data.versions || [];
      setVersions(reloadedVersions);
      const target = reloadedVersions.find((v: any) => v.id === selectedVersionId) || reloadedVersions.find((v: any) => v.isActive) || reloadedVersions[0];
      if (target) loadVersionContent(target);
    } catch (err: any) { message.error(err?.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleSaveAsNew = async () => {
    if (!selectedPrompt) return;
    const values = await form.validateFields();
    setSaving(true);
    try {
      await axiosInstance.patch(`${API_BASE_URL}/admin/llm/prompts/${selectedPrompt.id}`, values);
      const newContent = joinSections(sections);
      const resp = await axiosInstance.post(
        `${API_BASE_URL}/admin/llm/prompts/${selectedPrompt.id}/versions`,
        { content: newContent, changeNote: 'Edited via CMS', config: versionConfig },
      );
      const newVersionId = resp.data?.id || null;
      message.success('New version created');
      invalidate({ resource: 'llm-prompts', invalidates: ['list'] });
      const full = await axiosInstance.get(`${API_BASE_URL}/admin/llm/prompts/${selectedPrompt.id}`);
      setSelectedPrompt(full.data);
      const reloadedVersions = full.data.versions || [];
      setVersions(reloadedVersions);
      const target = (newVersionId && reloadedVersions.find((v: any) => v.id === newVersionId))
        || reloadedVersions.find((v: any) => v.isActive)
        || reloadedVersions[0];
      if (target) loadVersionContent(target);
    } catch (err: any) { message.error(err?.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleBulkDeleteVersions = async (promptId: string) => {
    const versionIds = selectedByPrompt[promptId] || [];
    if (versionIds.length === 0) return;
    setDeletingVersions(true);
    try {
      await Promise.all(
        versionIds.map((versionId) =>
          axiosInstance.delete(`${API_BASE_URL}/admin/llm/versions/${versionId}`),
        ),
      );
      message.success(`Deleted ${versionIds.length} version(s)`);
      setSelectedByPrompt((prev) => ({ ...prev, [promptId]: [] }));
      invalidate({ resource: 'llm-prompts', invalidates: ['list'] });
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Delete failed');
    } finally {
      setDeletingVersions(false);
    }
  };

  const handleDeletePrompt = async () => {
    if (!selectedPrompt) return;
    setSaving(true);
    try {
      await axiosInstance.delete(`${API_BASE_URL}/admin/llm/prompts/${selectedPrompt.id}`);
      message.success('Prompt deleted');
      invalidate({ resource: 'llm-prompts', invalidates: ['list'] });
      closeDrawer();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const handleActivateVersion = async (versionId: string) => {
    setSaving(true);
    try {
      await axiosInstance.patch(`${API_BASE_URL}/admin/llm/versions/${versionId}/activate`);
      message.success('Activated');
      const full = await axiosInstance.get(`${API_BASE_URL}/admin/llm/prompts/${selectedPrompt.id}`);
      setSelectedPrompt(full.data);
      setVersions(full.data.versions || []);
      const active = (full.data.versions || []).find((v: any) => v.isActive);
      if (active) loadVersionContent(active);
      invalidate({ resource: 'llm-prompts', invalidates: ['list'] });
    } catch (err: any) { message.error(err?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  // Test tab
  useEffect(() => {
    const vars: Record<string, string> = {};
    detectedVars.forEach(v => { vars[v] = testVars[v] || ''; });
    setTestVars(vars);
  }, [detectedVars.length]);

  const handleTestImage = useCallback(async (file: File) => {
    try {
      const resized = await resizeImageForLlm(file);
      setTestImageBase64(resized.base64);
      setTestImagePreview(`data:image/jpeg;base64,${resized.base64.slice(0, 100)}...`);
      message.info(`Resized: ${(file.size / 1024).toFixed(0)}KB → ${(resized.resizedSize / 1024).toFixed(0)}KB`);
    } catch { message.error('Image resize failed'); }
    return false;
  }, []);

  const handleTestRun = async () => {
    if (!selectedPrompt) return;
    if (testProviders.length === 0) { message.warning('Select at least one provider'); return; }
    setTestRunning(true);
    setTestResults([]);
    try {
      const payload: any = {
        promptSlug: selectedPrompt.slug,
        promptVariables: testVars,
        providerIds: testProviders,
      };
      if (testInputType === 'text' && testText.trim()) payload.text = testText;
      else if (testInputType === 'image' && testImageBase64) {
        payload.imageBase64 = testImageBase64;
        payload.imageMimeType = 'image/jpeg';
      }
      const resp = await axiosInstance.post(`${API_BASE_URL}/llm/playground`, payload);
      setTestResults(resp.data?.runs || []);
    } catch (err: any) { message.error(err?.response?.data?.message || 'Test failed'); }
    finally { setTestRunning(false); }
  };

  const activeVersion = versions.find((v: any) => v.id === selectedVersionId);
  const drawerTitle = (
    <Space>
      {selectedPrompt?.name || 'Loading...'}
      {activeVersion && <Tag style={tagStyle('#1890ff')}>v{activeVersion.version}</Tag>}
      {activeVersion?.isActive && <Tag style={tagStyle('#3ecf8e')}>Active</Tag>}
    </Space>
  );

  const drawerExtra = (
    <Space>
      <Popconfirm
        title="Delete this prompt?"
        description={`This deletes "${selectedPrompt?.slug}" and ALL its versions. Cannot be undone.`}
        okText="Delete prompt"
        okButtonProps={{ danger: true }}
        onConfirm={handleDeletePrompt}
      >
        <Button danger icon={<DeleteOutlined />} loading={saving}>Delete Prompt</Button>
      </Popconfirm>
      <Button onClick={closeDrawer}>Cancel</Button>
      <Button type="primary" loading={saving} onClick={handleSaveCurrent}>Save</Button>
      <Button icon={<ThunderboltOutlined />} loading={saving} onClick={handleSaveAsNew}>
        Save as New Version
      </Button>
    </Space>
  );

  return (
    <List resource="llm-prompts">
      <div style={{ marginBottom: 16 }}>
        <Input.Search prefix={<SearchOutlined />} placeholder="Search prompts..."
          defaultValue={searchText} onSearch={(v) => setSearchText(v)} allowClear style={{ width: 320 }} />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1000 }}
        expandable={{
          expandRowByClick: true,
          expandedRowRender: (record: any) => {
            const promptVersions = record.versions || [];
            const selectedKeys = selectedByPrompt[record.id] || [];
            return (
              <div style={{ padding: '8px 16px' }}>
                <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    Versions ({promptVersions.length})
                  </Typography.Text>
                  <Popconfirm
                    title={`Delete ${selectedKeys.length} version(s)?`}
                    description="This cannot be undone. Call logs referencing these versions will be kept."
                    onConfirm={() => handleBulkDeleteVersions(record.id)}
                    disabled={selectedKeys.length === 0}
                  >
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      disabled={selectedKeys.length === 0}
                      loading={deletingVersions}
                    >
                      Delete selected ({selectedKeys.length})
                    </Button>
                  </Popconfirm>
                </div>
                <Table
                  dataSource={promptVersions}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  rowSelection={{
                    selectedRowKeys: selectedKeys,
                    onChange: (keys) =>
                      setSelectedByPrompt((prev) => ({ ...prev, [record.id]: keys as string[] })),
                    getCheckboxProps: (v: any) => ({ disabled: v.isActive }),
                  }}
                >
                  <Table.Column
                    dataIndex="version"
                    title="Version"
                    width={140}
                    render={(v: number, row: any) => (
                      <Space>
                        <Tag style={tagStyle('#1890ff')}>v{v}</Tag>
                        {row.isActive && <Tag style={tagStyle('#3ecf8e')}>Active</Tag>}
                      </Space>
                    )}
                  />
                  <Table.Column
                    dataIndex="changeNote"
                    title="Note"
                    render={(n: string) => n || <span style={{ color: token.colorTextSecondary }}>—</span>}
                  />
                  <Table.Column
                    dataIndex="createdAt"
                    title="Created"
                    width={170}
                    render={(d: string) => new Date(d).toLocaleString()}
                  />
                  <Table.Column
                    title="Actions"
                    width={100}
                    render={(_: any, v: any) => (
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openDrawer(record, v.id)}
                      >
                        Open
                      </Button>
                    )}
                  />
                </Table>
              </div>
            );
          },
          rowExpandable: (record: any) => (record.versions?.length ?? 0) > 0,
        }}>
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column dataIndex="slug" title="Slug" />
        <Table.Column dataIndex="description" title="Description"
          render={(d: string) => d ? (d.length > 50 ? `${d.slice(0, 50)}...` : d) : '—'} />
        <Table.Column dataIndex="versions" title="Version"
          render={(versions: any[]) => {
            const active = versions?.find((v: any) => v.isActive);
            return active ? <Tag style={tagStyle('#1890ff')}>v{active.version}</Tag> : <Tag style={tagStyle('#898989')}>None</Tag>;
          }} />
        <Table.Column dataIndex="isActive" title="Status"
          render={(a: boolean) => <Tag style={tagStyle(a ? '#3ecf8e' : '#898989')}>{a ? 'Active' : 'Inactive'}</Tag>} />
      </Table>

      <Drawer title={drawerTitle} open={drawerOpen} onClose={closeDrawer} width={780} extra={drawerExtra}>
        {loading ? <Spin /> : selectedPrompt ? (
          <>
            <Form form={form} layout="vertical" size="small">
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <Form.Item label="Name" name="name" rules={[{ required: true }]} style={{ flex: 1, marginBottom: 0 }}>
                  <Input />
                </Form.Item>
                <Form.Item label="Slug" name="slug" style={{ flex: 1, marginBottom: 0 }}>
                  <Input />
                </Form.Item>
                <Form.Item label="Active" name="isActive" valuePropName="checked" style={{ width: 80, marginBottom: 0 }}>
                  <Switch />
                </Form.Item>
              </div>
              <Form.Item label="Description" name="description" style={{ marginBottom: 12 }}>
                <Input.TextArea rows={2} />
              </Form.Item>
            </Form>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              <Typography.Text strong style={{ fontSize: 13 }}>Version</Typography.Text>
              <Select value={selectedVersionId || undefined} onChange={handleVersionChange} style={{ flex: 1 }}
                optionLabelProp="trigger"
                options={versions.map((v: any) => ({
                  value: v.id,
                  trigger: (
                    <Space size={4}>
                      <Tag style={{ ...tagStyle('#1890ff'), margin: 0 }}>v{v.version}</Tag>
                      {v.isActive && <Tag style={{ ...tagStyle('#3ecf8e'), margin: 0 }}>Active</Tag>}
                    </Space>
                  ),
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Tag style={{ ...tagStyle('#1890ff'), margin: 0 }}>v{v.version}</Tag>
                      {v.isActive && <Tag style={{ ...tagStyle('#3ecf8e'), margin: 0 }}>Active</Tag>}
                      <span style={{ color: token.colorTextSecondary, fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                        {v.changeNote ? (v.changeNote.length > 40 ? `${v.changeNote.slice(0, 40)}…` : v.changeNote) : 'No note'}
                      </span>
                      <span style={{ color: token.colorTextSecondary, fontSize: 11, flexShrink: 0 }}>
                        {new Date(v.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  ),
                }))} />
            </div>

            <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
              {
                key: 'edit',
                label: 'Edit',
                children: (
                  <div>
                    <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Switch checked={!!versionConfig?.enableThinking}
                        onChange={(v) => setVersionConfig((c: any) => ({ ...c, enableThinking: v }))} />
                      <div>
                        <Typography.Text strong style={{ fontSize: 13 }}>Enable Thinking</Typography.Text>
                        <br />
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                          Model reasons before answering. Increases latency but may improve accuracy.
                        </Typography.Text>
                      </div>
                    </div>
                    <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                      <Typography.Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                        Provider & Model {providers.length === 0 && <Tag color="warning">No providers loaded</Tag>}
                      </Typography.Text>
                      <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <Select
                            value={versionConfig?.providerId || undefined}
                            onChange={(v) => setVersionConfig((c: any) => {
                              const next = { ...c, providerId: v };
                              if (v !== c?.providerId) delete next.model;
                              return next;
                            })}
                            allowClear
                            placeholder="Default (highest priority)"
                            style={{ width: '100%' }}
                            options={providerOptions}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Select
                            value={versionConfig?.model || undefined}
                            onChange={(v) => setVersionConfig((c: any) => ({ ...c, model: v }))}
                            allowClear
                            placeholder={selectedProvider ? selectedProvider.defaultModel : 'Provider default'}
                            style={{ width: '100%' }}
                            options={modelOptions.map((m: string) => ({ value: m, label: m }))}
                          />
                        </div>
                      </div>
                      <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                        Select provider to override default. Model uses provider's default if not set.
                      </Typography.Text>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <Typography.Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>System Prompt</Typography.Text>
                      <Input.TextArea value={sections.system} onChange={(e) => setSections(s => ({ ...s, system: e.target.value }))}
                        rows={12} style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}
                        placeholder="Main instructions for the AI model..." />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <Typography.Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>User Prompt</Typography.Text>
                      <Input.TextArea value={sections.user} onChange={(e) => setSections(s => ({ ...s, user: e.target.value }))}
                        rows={4} style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}
                        placeholder="Template for user message. Use {{VARIABLE}} for dynamic content." />
                    </div>
                    <div>
                      <Typography.Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Output Structure</Typography.Text>
                      <Input.TextArea value={sections.output} onChange={(e) => setSections(s => ({ ...s, output: e.target.value }))}
                        rows={8} style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}
                        placeholder="Expected JSON output schema / format instructions..." />
                    </div>
                    {detectedVars.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>Variables: {detectedVars.map(v => `{{${v}}}`).join(', ')}</Typography.Text>
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'test',
                label: 'Test',
                children: (
                  <div>
                    {detectedVars.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>Variables</Typography.Text>
                        {detectedVars.map(v => (
                          <Input key={v} placeholder={`{{${v}}}`} value={testVars[v] || ''}
                            onChange={(e) => setTestVars(prev => ({ ...prev, [v]: e.target.value }))}
                            addonBefore={v} style={{ marginBottom: 8 }} />
                        ))}
                      </div>
                    )}
                    <div style={{ marginBottom: 16 }}>
                      <Typography.Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>Input</Typography.Text>
                      <Radio.Group value={testInputType} onChange={(e) => setTestInputType(e.target.value)} style={{ marginBottom: 8 }}>
                        <Radio value="text">Text</Radio>
                        <Radio value="image">Image</Radio>
                      </Radio.Group>
                      {testInputType === 'text' ? (
                        <Input.TextArea rows={4} placeholder="Enter text input..." value={testText} onChange={(e) => setTestText(e.target.value)} />
                      ) : (
                        <Upload.Dragger accept="image/*" maxCount={1}
                          beforeUpload={(f) => { handleTestImage(f); return false; }}
                          onRemove={() => { setTestImageBase64(undefined); setTestImagePreview(undefined); }}>
                          <UploadOutlined style={{ fontSize: 24, color: token.colorTextSecondary }} />
                          <p>Drop image (auto-resized to 1024px)</p>
                        </Upload.Dragger>
                      )}
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <Typography.Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>Providers</Typography.Text>
                      <Checkbox.Group value={testProviders} onChange={(v) => setTestProviders(v as string[])}>
                        <Space direction="vertical">
                          {providers.map((p: any) => (
                            <Checkbox key={p.id} value={p.id}>{p.name}</Checkbox>
                          ))}
                        </Space>
                      </Checkbox.Group>
                    </div>
                    <Button type="primary" icon={<ThunderboltOutlined />} loading={testRunning} onClick={handleTestRun} block size="large">
                      Run Test
                    </Button>
                    {testResults.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <Table dataSource={testResults} rowKey={(r: any) => r.providerId + r.model} pagination={false} size="small"
                          expandable={{ expandedRowRender: (r: any) => (
                            <pre style={{ background: token.colorBgLayout, padding: 12, borderRadius: 8, whiteSpace: 'pre-wrap', fontSize: 12, maxHeight: 400, overflow: 'auto' }}>
                              {r.result ? (typeof r.result === 'string' ? r.result : JSON.stringify(r.result, null, 2)) : r.error || 'No response'}
                            </pre>
                          )}}>
                          <Table.Column dataIndex="providerName" title="Provider" />
                          <Table.Column dataIndex="model" title="Model" />
                          <Table.Column dataIndex="status" title="Status" width={80}
                            render={(s: string) => <Tag style={tagStyle(s === 'success' ? '#52c41a' : '#ff4d4f')}>{s}</Tag>} />
                          <Table.Column dataIndex="latencyMs" title="ms" width={80} render={(m: number) => m ? `${m}` : '—'} />
                          <Table.Column dataIndex="inputTokens" title="In" width={70} render={(v: number) => v ?? '—'} />
                          <Table.Column dataIndex="outputTokens" title="Out" width={70} render={(v: number) => v ?? '—'} />
                        </Table>
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'history',
                label: 'History',
                children: (
                  <Collapse size="small" items={versions.map((v: any) => ({
                    key: v.id,
                    label: (
                      <Space>
                        <Tag style={tagStyle('#1890ff')}>v{v.version}</Tag>
                        {v.isActive && <Tag style={tagStyle('#3ecf8e')} icon={<CheckCircleOutlined />}>Active</Tag>}
                        <span style={{ color: token.colorTextSecondary, fontSize: 12 }}>{v.changeNote || 'No note'}</span>
                        <span style={{ color: token.colorTextSecondary, fontSize: 12 }}>{new Date(v.createdAt).toLocaleString()}</span>
                      </Space>
                    ),
                    children: (
                      <div>
                        <Button size="small" type="link" onClick={() => loadVersionContent(v)}>Load into editor</Button>
                        {!v.isActive && <Button size="small" type="primary" loading={saving} onClick={() => handleActivateVersion(v.id)} style={{ marginLeft: 8 }}>Activate</Button>}
                        <pre style={{ background: token.colorBgLayout, padding: 12, borderRadius: 8, whiteSpace: 'pre-wrap', fontSize: 12, maxHeight: 300, overflow: 'auto', marginTop: 8 }}>
                          {v.content}
                        </pre>
                      </div>
                    ),
                  }))} />
                ),
              },
            ]} />
          </>
        ) : null}
      </Drawer>
    </List>
  );
};
